import { ActionRunsService } from './action-runs-service';
import { ApiError, api } from './api-client';

type SuccessEnvelope<T> = { data: T };

// Spend preview for the org-level "Watch Target Accounts" schedule — how many
// accounts it covers (minus per-account opt-outs), the per-account Action cost,
// and the resulting weekly Actions spend. Costs come from the backend estimate,
// never hardcoded here.
export type AccountWatchEstimate = {
  account_count: number;
  watched_count: number;
  actions_per_account: number;
  weekly_actions: number;
};

// Result of a manual per-account refresh: how many accounts were refreshed, how
// many external signals the diff emitted, and any per-account failures.
export type AccountWatchRefreshResult = {
  refreshed: number;
  signals: number;
  failures: number;
};

// Account Watch endpoints (org owner/admin, session auth, `{ data }` envelope).
// Mirrors the backend admin routes under /admin/actions/account-watch.
export const AccountWatchService = {
  // GET /admin/actions/account-watch/estimate — the Watch spend preview.
  estimate: async (): Promise<AccountWatchEstimate> => {
    const r = await api.get<SuccessEnvelope<AccountWatchEstimate>>(
      '/admin/actions/account-watch/estimate',
    );
    return r.data;
  },

  // POST /admin/actions/account-watch starts a background check of one Target
  // Account; this waits for it.
  refresh: async (profileId: string): Promise<AccountWatchRefreshResult> => {
    const r = await api.post<SuccessEnvelope<{ run_id: string }>>(
      '/admin/actions/account-watch',
      { profile_id: profileId },
    );
    const run = await ActionRunsService.waitFor(
      r.data.run_id,
      'The check is still running. New signals will appear when it finishes.',
    );
    if (run.status !== 'succeeded') {
      throw new ApiError(500, run.error ?? 'The check failed. Try again.');
    }
    return run.result as AccountWatchRefreshResult;
  },
};
